import React, {FC} from 'react';

import {defineMessage, Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {setMetricDetailsAction} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {
  BasicReportProps,
  ContributingSegmentsOptionsEnum,
  ReportElementIdsEnum,
  ReportFilterOptionsEnum,
} from '../../../helpers/analyticsReportHelpers';
import {AnalyticsComputedProgressMetricsEnum} from '../../../transformers/analyticsMetricsTransformersTypes';
import {ReportProgressBar} from '../../reportElements/charts/reportProgressBar';
import {ReportTable} from '../../reportElements/table/reportTable';
import {teamColumnId} from '../../reportElements/table/tableHelpers';

/*
 * Constants.
 */

const defaultTagPerfColumnIds = [
  'num_active_segments',
  'pct_active_segments',
  'avg_reply_time',
  'avg_handle_time',
  'avg_resolution_time',
  'num_segments_open_at_end',
];
const defaultTagChangesColumnIds = ['num_active_segments', 'num_added', 'num_removed', 'avg_tag_duration'];

const progressBarLegendRenderers = [
  (val: number) => <Trans id="tags-pct-tagged-legend-label">{val}% tagged</Trans>,
  (val: number) => <Trans id="tags-pct-untagged-legend-label">{val}% untagged</Trans>,
];

enum TagBarSubmetricsEnum {
  TAGGED = 'tagged',
  UNTAGGED = 'notTagged',
}

/*
 * Props.
 */

interface TagsReportProps extends BasicReportProps {
  isTeamColumnVisible: boolean;
}

/*
 * Component.
 */

export const TagsReport: FC<TagsReportProps> = ({isLoading, isTeamColumnVisible}) => {
  const dispatch = useAppDispatch();
  const localize = useLocalize();

  const onSerieSelect = (serieIndex: number) => {
    const isTaggedSerie = serieIndex === 0;
    dispatch(
      setMetricDetailsAction(
        AnalyticsScalarMetricsEnum.PCT_TAGGED_CONVERSATIONS,
        {
          [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
            [ContributingSegmentsOptionsEnum.SUB_METRIC]: isTaggedSerie
              ? TagBarSubmetricsEnum.TAGGED
              : TagBarSubmetricsEnum.UNTAGGED,
          },
        },
        isTaggedSerie
          ? localize(
              defineMessage({
                id: 'metric-details-tagged-convos',
                message: 'Tagged conversations',
              }),
            )
          : localize(
              defineMessage({
                id: 'metric-details-untagged-convos',
                message: 'Untagged conversations',
              }),
            ),
      ),
    );
  };

  return (
    <>
      {/* Conversations with tags */}
      <ReportProgressBar
        reportSectionId={ReportElementIdsEnum.TAGS_CONVERSATIONS_PCT}
        isLoading={isLoading}
        metric={AnalyticsComputedProgressMetricsEnum.TAG_REPORT_PROGRESS}
        legendRenderers={progressBarLegendRenderers}
        onSerieSelect={onSerieSelect}
      />

      {/* Individual Tag Performance */}
      <ReportTable
        reportSectionId={ReportElementIdsEnum.TAGS_PERFORMANCE_TABLE}
        isLoading={isLoading}
        metric={AnalyticsTableMetricsEnum.TAGS_ALL_TABLE}
        defaultColumnIds={defaultTagPerfColumnIds}
        blockedColumnIds={isTeamColumnVisible ? undefined : [teamColumnId]}
        disableSelection
        initialState={{
          sortBy: [
            {
              id: 'num_active_segments',
              desc: true,
            },
          ],
        }}
      />

      {/* Tag Changes */}
      <ReportTable
        reportSectionId={ReportElementIdsEnum.TAG_CHANGES_TABLE}
        isLoading={isLoading}
        metric={AnalyticsTableMetricsEnum.TAG_CHANGES_TABLE}
        defaultColumnIds={defaultTagChangesColumnIds}
        disableSelection
        blockedColumnIds={isTeamColumnVisible ? undefined : [teamColumnId]}
        initialState={{
          sortBy: [
            {
              id: 'num_removed',
              desc: true,
            },
          ],
        }}
      />
    </>
  );
};
